import { useCallback, useState } from 'react';
import { adminLogin, enrollFirstTime, verifyMfa, logoutRequest, getMe } from '@/api/endpoints/auth';
import type { EnrollResponse, MeResponse } from '@/api/schemas/auth';
import { ApiError } from '@/api/errors';
import { problemCode } from '@/api/problem';
import { useAuthStore } from './auth-store';
import { roleFromToken } from './jwt-claims';
import type { AdminUser } from './types';

/**
 * The administration door's rejections, which are ALL 401s and used to be four
 * indistinguishable ones. Each has a different recovery, and the wording is the
 * recovery:
 *
 *   - `ADMIN_LOGIN_INVALID` — one code for unknown email, wrong password, a
 *     non-staff role and a disabled account, deliberately. Telling them apart
 *     is user enumeration, so the message must not try either.
 *   - `MFA_CODE_INVALID` — the challenge is still alive; retype the digits.
 *   - `MFA_CHALLENGE_INVALID` — no code can fix this one; start over.
 */
const AUTH_MESSAGES: Record<string, string> = {
  ADMIN_LOGIN_INVALID: 'Correo o contraseña incorrectos.',
  MFA_CODE_INVALID:
    'El código no coincide. Comprueba que la hora de tu teléfono esté sincronizada y escribe el que muestre ahora la app.',
  MFA_CHALLENGE_INVALID:
    'La verificación caducó. Vuelve a entrar con tu correo y contraseña para empezar de nuevo.',
};

function authErrorText(e: unknown): string {
  const code = problemCode(e);
  if (code !== null && AUTH_MESSAGES[code]) return AUTH_MESSAGES[code];
  return e instanceof ApiError ? e.message : 'Error de autenticación';
}

const KNOWN_ROLES: AdminUser['role'][] = ['ADMIN', 'STAFF', 'USER'];
function asAdminRole(role: string | null): AdminUser['role'] {
  return KNOWN_ROLES.includes(role as AdminUser['role']) ? (role as AdminUser['role']) : 'ADMIN';
}

// Mapea la respuesta de /me a la identidad del panel (name = displayName).
function userFromMe(me: MeResponse): AdminUser {
  return {
    id: me.id,
    email: me.email,
    role: asAdminRole(me.role),
    name: me.displayName ?? undefined,
  };
}

function userFromSession(accessToken: string, email: string): AdminUser {
  // Identidad mínima por claim del access token — se usa como fallback inmediato
  // (y si GET /auth/me falla). El id real llega luego desde /me.
  return { id: email, email, role: asAdminRole(roleFromToken(accessToken)) };
}

export function useLoginFlow() {
  const [enrollData, setEnrollData] = useState<EnrollResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setPending] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaMode, setMfaMode] = useState<'enroll' | 'verify'>('verify');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string>('');

  const finish = useCallback(async (token: string, code: string, email: string) => {
    const tokens = await verifyMfa({ mfaChallengeToken: token, code });
    // Fijamos primero la sesión con la identidad por claim (deja el Bearer vivo
    // para /me) y luego la sustituimos por la identidad real. Si /me falla,
    // se conserva el fallback.
    useAuthStore.getState().setSession(tokens, userFromSession(tokens.accessToken, email));
    try {
      const me = await getMe();
      useAuthStore.getState().setUser(userFromMe(me));
    } catch {
      /* /me best-effort: mantenemos la identidad por claim */
    }
  }, []);

  // Paso 1: valida credenciales y abre el modal MFA (enroll si es la 1ª vez).
  const submitCredentials = useCallback(async (email: string, password: string) => {
    setError(null);
    setPending(true);
    try {
      const res = await adminLogin({ email, password });
      setChallenge(res.mfaChallengeToken);
      setPendingEmail(email);
      if (!res.mfaEnrolled) {
        const data = await enrollFirstTime(res.mfaChallengeToken);
        setEnrollData(data);
        setMfaMode('enroll');
      } else {
        setEnrollData(null);
        setMfaMode('verify');
      }
      setMfaOpen(true);
    } catch (e) {
      setError(authErrorText(e));
    } finally {
      setPending(false);
    }
  }, []);

  // Paso 2: completa el MFA con el código del modal.
  const submitCode = useCallback(
    async (code: string) => {
      if (!challenge) return;
      setError(null);
      setPending(true);
      try {
        await finish(challenge, code, pendingEmail);
        setMfaOpen(false);
      } catch (e) {
        // A dead challenge cannot be repaired by any code, so leaving the modal
        // open would be a box that can never be right. A wrong code is the
        // opposite: the challenge is alive and the only thing to do is retype,
        // which is why the two are separate codes rather than one 401.
        if (problemCode(e) === 'MFA_CHALLENGE_INVALID') {
          setMfaOpen(false);
          setChallenge(null);
          setEnrollData(null);
        }
        setError(authErrorText(e));
      } finally {
        setPending(false);
      }
    },
    [challenge, pendingEmail, finish],
  );

  // Cerrar el modal sin autenticar: vuelve al paso 1 y limpia el challenge.
  const cancelMfa = useCallback(() => {
    setMfaOpen(false);
    setChallenge(null);
    setEnrollData(null);
    setPendingEmail('');
    setError(null);
  }, []);

  return {
    submitCredentials,
    submitCode,
    cancelMfa,
    mfaOpen,
    mfaMode,
    enrollData,
    error,
    isPending,
  };
}

export function useLogout() {
  return useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      /* best-effort */
    } finally {
      useAuthStore.getState().clear();
    }
  }, []);
}
